<template><div><code-example source-code='&lt;dt-combobox-with-popover
  label=&quot;Label Text&quot;
  :show-list=&quot;showList&quot;
  content-width=&quot;anchor&quot;
  max-height=&quot;300px&quot;
  @select=&quot;onSelect&quot;
  @opened=&quot;onOpened&quot;
&gt;
  &lt;template #input=&quot;{ inputProps, onInput }&quot;&gt;
    &lt;dt-input
      v-model=&quot;inputValue&quot;
      placeholder=&quot;Select one or start typing&quot;
      v-bind=&quot;inputProps&quot;
      @update:model-value=&quot;onInput&quot;
    /&gt;
  &lt;/template&gt;
  &lt;template #list=&quot;{ listProps }&quot;&gt;
    &lt;ul v-bind=&quot;listProps&quot; class=&quot;d-p-50&quot;&gt;
      &lt;dt-list-item
        v-for=&quot;(item, i) in items&quot;
        :key=&quot;item.id&quot;
        role=&quot;option&quot;
        navigation-type=&quot;arrow-keys&quot;
        @click=&quot;onSelect(i)&quot;
      &gt;
        {{ item.number }}
        &lt;template #right&gt;
          &lt;span class=&quot;d-fc-secondary&quot;&gt;{{ item.type }}&lt;/span&gt;
        &lt;/template&gt;
      &lt;/dt-list-item&gt;
    &lt;/ul&gt;
  &lt;/template&gt;
&lt;/dt-combobox-with-popover&gt;'>
<dt-combobox-with-popover
  label="Label Text"
  :show-list="showList"
  content-width="anchor"
  max-height="300px"
  @select="onSelect"
  @opened="onOpened"
>
  <template #input="{ inputProps, onInput }">
    <dt-input
      v-model="inputValue"
      placeholder="Select one or start typing"
      v-bind="inputProps"
      @update:model-value="onInput"
    />
  </template>
  <template #list="{ listProps }">
    <ul v-bind="listProps" class="d-p-50">
      <dt-list-item
        v-for="(item, i) in items"
        :key="item.id"
        role="option"
        navigation-type="arrow-keys"
        @click="onSelect(i)"
      >
        {{ item.number }}
        <template #right>
          <span class="d-fc-secondary">{{ item.type }}</span>
        </template>
      </dt-list-item>
    </ul>
  </template>
</dt-combobox-with-popover>
</code-example>
<!-- <component-combinator component-name="DtComboboxWithPopover" /> -->
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The combobox contains the <code v-pre>input</code> and <code v-pre>list</code> slots specified in the <a href="/components/combobox.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">combobox documentation</a>.
Since it's using the popover content to render the list, it's also possible to add a header and footer to the popover
as specified in the <a href="/components/popover.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">popover documentation</a>.</p>
<h3 id="closing-the-list-after-selection" tabindex="-1" class="d-docsite--header-3">Closing the list after selection <a class="header-anchor d-link d-docsite--link d-link" href="#closing-the-list-after-selection" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">When not passing <code v-pre>showList</code> and using the default combobox trigger on input focus,
to close the list with the <code v-pre>select</code> event or with the <code v-pre>click</code> event in the list item,
use the <code v-pre>closeComboboxList</code> method:</p>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token literal-property property">methods</span><span class="token operator">:</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token function">onSelect</span> <span class="token punctuation">(</span><span class="token parameter">i</span><span class="token punctuation">)</span> <span class="token punctuation">{</span></span>
<span class="line">    <span class="token keyword">this</span><span class="token punctuation">.</span>$refs<span class="token punctuation">.</span>comboboxWithPopover<span class="token punctuation">.</span><span class="token function">closeComboboxList</span><span class="token punctuation">(</span><span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line">  <span class="token punctuation">}</span><span class="token punctuation">,</span></span>
<span class="line"><span class="token punctuation">}</span></span>
<span class="line"></span></code></pre>
</div><h2 id="with-header-and-footer" tabindex="-1" class="d-docsite--header-2">With Header and Footer <a class="header-anchor d-link d-docsite--link d-link" href="#with-header-and-footer" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">You can add header and footer content to the popover using the <code v-pre>header</code> and <code v-pre>footer</code> slots.</p>
<code-example source-code='&lt;dt-combobox-with-popover
  label=&quot;Label Text&quot;
  :show-list=&quot;showListHeaderFooter&quot;
  content-width=&quot;anchor&quot;
  max-height=&quot;300px&quot;
  @select=&quot;onSelectHeaderFooter&quot;
  @opened=&quot;onOpenedHeaderFooter&quot;
&gt;
  &lt;template #header&gt;
    &lt;div class=&quot;d-px-150 d-py-100 d-fw-semibold&quot;&gt;Select an option&lt;/div&gt;
  &lt;/template&gt;
  &lt;template #input=&quot;{ inputProps, onInput }&quot;&gt;
    &lt;dt-input
      v-model=&quot;inputValueHeaderFooter&quot;
      placeholder=&quot;Select one or start typing&quot;
      v-bind=&quot;inputProps&quot;
      @update:model-value=&quot;onInput&quot;
    /&gt;
  &lt;/template&gt;
  &lt;template #list=&quot;{ listProps }&quot;&gt;
    &lt;ul v-bind=&quot;listProps&quot; class=&quot;d-p-50&quot;&gt;
      &lt;dt-list-item
        v-for=&quot;(item, i) in items&quot;
        :key=&quot;item.id&quot;
        role=&quot;option&quot;
        navigation-type=&quot;arrow-keys&quot;
        @click=&quot;onSelectHeaderFooter(i)&quot;
      &gt;
        {{ item.number }}
        &lt;template #right&gt;
          &lt;span class=&quot;d-fc-secondary&quot;&gt;{{ item.type }}&lt;/span&gt;
        &lt;/template&gt;
      &lt;/dt-list-item&gt;
    &lt;/ul&gt;
  &lt;/template&gt;
  &lt;template #footer&gt;
    &lt;div class=&quot;d-px-150 d-py-100 d-fc-tertiary&quot;&gt;Footer content&lt;/div&gt;
  &lt;/template&gt;
&lt;/dt-combobox-with-popover&gt;'>
<dt-combobox-with-popover
  label="Label Text"
  :show-list="showListHeaderFooter"
  content-width="anchor"
  max-height="300px"
  @select="onSelectHeaderFooter"
  @opened="onOpenedHeaderFooter"
>
  <template #header>
    <div class="d-px-150 d-py-100 d-fw-semibold">Select an option</div>
  </template>
  <template #input="{ inputProps, onInput }">
    <dt-input
      v-model="inputValueHeaderFooter"
      placeholder="Select one or start typing"
      v-bind="inputProps"
      @update:model-value="onInput"
    />
  </template>
  <template #list="{ listProps }">
    <ul v-bind="listProps" class="d-p-50">
      <dt-list-item
        v-for="(item, i) in items"
        :key="item.id"
        role="option"
        navigation-type="arrow-keys"
        @click="onSelectHeaderFooter(i)"
      >
        {{ item.number }}
        <template #right>
          <span class="d-fc-secondary">{{ item.type }}</span>
        </template>
      </dt-list-item>
    </ul>
  </template>
  <template #footer>
    <div class="d-px-150 d-py-100 d-fc-tertiary">Footer content</div>
  </template>
</dt-combobox-with-popover>
</code-example>
<h2 id="content-mode" tabindex="-1" class="d-docsite--header-2">Content Mode <a class="header-anchor d-link d-docsite--link d-link" href="#content-mode" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Combobox popover content renders outside the DOM tree. Use the <code v-pre>contentMode</code> prop to apply color mode (invert, light, dark) to the positioned content. See <a href="/components/mode-island.html#positioned-components" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Positioned Components</a> for details.</p>
<code-example source-code='&lt;dt-combobox-with-popover content-mode=&quot;invert&quot;&gt;...&lt;/dt-combobox-with-popover&gt;
&lt;dt-combobox-with-popover content-mode=&quot;dark&quot;&gt;...&lt;/dt-combobox-with-popover&gt;
&lt;dt-combobox-with-popover content-mode=&quot;light&quot;&gt;...&lt;/dt-combobox-with-popover&gt;'>
<dt-combobox-with-popover
  content-mode="invert"
  label="Inverted Combobox"
  content-width="anchor"
  max-height="300px"
>
  <template #input="{ inputProps }">
    <dt-input placeholder="Type to search..." v-bind="inputProps" />
  </template>
  <template #list="{ listProps }">
    <ul v-bind="listProps">
      <dt-list-item navigation-type="arrow-keys" role="option">Option 1</dt-list-item>
      <dt-list-item navigation-type="arrow-keys" role="option">Option 2</dt-list-item>
      <dt-list-item navigation-type="arrow-keys" role="option">Option 3</dt-list-item>
    </ul>
  </template>
</dt-combobox-with-popover>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="comboboxwithpopover" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">A screen reader visible only close button is added by default.</p>
</div></template>


<script setup>
import { ref } from 'vue';

const ITEMS_DATA = [
  { id: 'item1', number: '(732) 338-2720', type: 'MAINLINE' },
  { id: 'item2', number: '(732) 338-2722', type: 'MAINLINE' },
  { id: 'item3', number: '(732) 338-2725', type: 'MAINLINE' },
  { id: 'item4', number: '(732) 338-2764', type: 'MAINLINE' },
  { id: 'item5', number: '(732) 338-2784', type: 'MAINLINE' },
  { id: 'item6', number: '(732) 338-2743', type: 'MAINLINE' },
];

const items = ref([...ITEMS_DATA]);
const inputValue = ref('');
const showList = ref(null);

const inputValueHeaderFooter = ref('');
const showListHeaderFooter = ref(null);

function onSelect (i) {
  if (items.value[i]) {
    inputValue.value = items.value[i].number;
  }
}

function onOpened (opened) {
  if (!opened) {
    showList.value = null;
  }
}

function onSelectHeaderFooter (i) {
  if (items.value[i]) {
    inputValueHeaderFooter.value = items.value[i].number;
  }
}

function onOpenedHeaderFooter (opened) {
  if (!opened) {
    showListHeaderFooter.value = null;
  }
}
</script>